// Client-side image resize for library cover uploads.
//
// Targets a max long-edge (default 600 px) so we don't store full-resolution
// phone photos as cover images. Re-encodes to JPEG by default; PNG sources
// with transparency are kept as PNG.

export type ResizeOptions = {
  maxEdge?: number;
  quality?: number;
};

const SKIP_RESIZE_BYTES = 200 * 1024;

const loadBitmap = async (file: File): Promise<{ width: number; height: number; draw: (ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, w: number, h: number) => void; close: () => void }> => {
  if (typeof createImageBitmap === "function") {
    const bmp = await createImageBitmap(file);
    return {
      width: bmp.width,
      height: bmp.height,
      draw: (ctx, w, h) => ctx.drawImage(bmp, 0, 0, w, h),
      close: () => bmp.close?.(),
    };
  }
  // Fallback: HTMLImageElement
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("Failed to decode image"));
      el.src = url;
    });
    return {
      width: img.naturalWidth,
      height: img.naturalHeight,
      draw: (ctx, w, h) => ctx.drawImage(img, 0, 0, w, h),
      close: () => URL.revokeObjectURL(url),
    };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
};

const hasAlpha = (mime: string): boolean => mime === "image/png" || mime === "image/webp";

export const resizeImageForCover = async (
  file: File,
  { maxEdge = 600, quality = 0.82 }: ResizeOptions = {},
): Promise<File> => {
  const bmp = await loadBitmap(file);
  try {
    const longEdge = Math.max(bmp.width, bmp.height);
    if (longEdge <= maxEdge && file.size <= SKIP_RESIZE_BYTES) {
      return file;
    }
    const scale = Math.min(1, maxEdge / longEdge);
    const targetW = Math.max(1, Math.round(bmp.width * scale));
    const targetH = Math.max(1, Math.round(bmp.height * scale));

    // Keep PNG only if source likely has transparency; otherwise re-encode JPEG.
    const outMime = hasAlpha(file.type) && file.type === "image/png" ? "image/png" : "image/jpeg";
    const outExt = outMime === "image/png" ? "png" : "jpg";

    let blob: Blob;
    if (typeof OffscreenCanvas !== "undefined") {
      const canvas = new OffscreenCanvas(targetW, targetH);
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas 2D context unavailable");
      bmp.draw(ctx, targetW, targetH);
      blob = await canvas.convertToBlob({ type: outMime, quality });
    } else {
      const canvas = document.createElement("canvas");
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas 2D context unavailable");
      bmp.draw(ctx, targetW, targetH);
      blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("Canvas encoding failed"))),
          outMime,
          quality,
        );
      });
    }

    const baseName = file.name.replace(/\.[^.]+$/, "") || "cover";
    return new File([blob], `${baseName}.${outExt}`, { type: outMime, lastModified: Date.now() });
  } finally {
    bmp.close();
  }
};
